import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Sparkles, Image as ImageIcon, Trash2, Edit, ArrowLeft,
  Menu, X, Users, Loader2, Star, RefreshCw, Video, Play, Pause,
  Mic, Volume2, Download
} from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import Sidebar from "./Sidebar";
import LanguageSwitcher from "./LanguageSwitcher";
import ImageCarousel from "./ImageCarousel";
import LoraTraining from "./LoraTraining";
import { API } from "../../lib/api";
import { useLang } from "../../lib/LanguageContext";

const ModelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [model, setModel] = useState(null);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [generatingLipsync, setGeneratingLipsync] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [lipsyncDialogOpen, setLipsyncDialogOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [videoPrompt, setVideoPrompt] = useState('gentle natural movement, cinematic lighting');
  const [videoModel, setVideoModel] = useState('kling');
  const [selectedImageForVideo, setSelectedImageForVideo] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [ttsText, setTtsText] = useState('');
  const [ttsVoice, setTtsVoice] = useState('nova');
  const [ttsModel, setTtsModel] = useState('tts-1-hd');
  const [ttsProvider, setTtsProvider] = useState('openai'); // 'openai' or 'elevenlabs'
  const [elevenVoiceId, setElevenVoiceId] = useState('0jvpZ98RZwx5FBOSZAc3');
  const [generatingTts, setGeneratingTts] = useState(false);
  const [ttsAudioPreview, setTtsAudioPreview] = useState(null);
  const [talkingDialogOpen, setTalkingDialogOpen] = useState(false);
  const [talkingText, setTalkingText] = useState('');
  const [talkingVoice, setTalkingVoice] = useState('nova');
  const [talkingProvider, setTalkingProvider] = useState('openai');
  const [talkingElevenVoiceId, setTalkingElevenVoiceId] = useState('0jvpZ98RZwx5FBOSZAc3');
  const [talkingMovement, setTalkingMovement] = useState('gentle natural head movement, subtle body sway, cinematic');
  const [generatingTalking, setGeneratingTalking] = useState(false);
  const [isMatureGeneration, setIsMatureGeneration] = useState(false);
  const [useNsfwModel, setUseNsfwModel] = useState(false);
  const [useExplicitNudity, setUseExplicitNudity] = useState(false);
  const [envImage, setEnvImage] = useState(null); // {id, preview, filename}
  const [uploadingEnv, setUploadingEnv] = useState(false);
  const [activeTab, setActiveTab] = useState('images'); // images or videos
  const [useBodyRef, setUseBodyRef] = useState(false);
  const [uploadingBodyRef, setUploadingBodyRef] = useState(false);
  const [bodyRefPreviews, setBodyRefPreviews] = useState([]); // [{id, image_base64}]
  const [bodyRefStrength, setBodyRefStrength] = useState(65); // 0-100, maps to 0-1 for API
  const [naturalQuality, setNaturalQuality] = useState(false);
  const [strictFaceMode, setStrictFaceMode] = useState(false);
  const [hardNudity, setHardNudity] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // {completed, total, status}
  const [selfiePickerOpen, setSelfiePickerOpen] = useState(false);

  const fetchModelData = useCallback(async () => {
    setLoading(true);
    try {
      const [modelRes, imagesRes, videosRes] = await Promise.all([
        axios.get(`${API}/models/${id}`),
        axios.get(`${API}/models/${id}/images`),
        axios.get(`${API}/models/${id}/videos`)
      ]);
      setModel(modelRes.data);
      setImages(imagesRes.data);
      setVideos(videosRes.data);
      
      // Load body reference previews
      const refs = modelRes.data.body_references || [];
      if (refs.length > 0) {
        const previews = await Promise.all(
          refs.map(async (ref) => {
            try {
              const res = await axios.get(`${API}/body-references/${ref.id}/raw`, { responseType: 'arraybuffer' });
              const b64 = btoa(new Uint8Array(res.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));
              return { id: ref.id, filename: ref.filename, image_base64: b64 };
            } catch { return { id: ref.id, filename: ref.filename, image_base64: null }; }
          })
        );
        setBodyRefPreviews(previews);
      } else {
        setBodyRefPreviews([]);
      }
      
      // Auto-poll for any processing videos
      const processingVideos = videosRes.data.filter(v => v.status === 'processing' || v.status === 'queued');
      processingVideos.forEach(v => pollVideoStatus(v.id));
    } catch (error) {
      toast.error(t('errorLoading'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchModelData();
  }, [fetchModelData]);

  const filteredBodyRefs = useMemo(() => 
    bodyRefPreviews.filter(r => r.image_base64), [bodyRefPreviews]
  );

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateDialogOpen(false);
    try {
      const payload = {
        prompt: customPrompt,
        is_mature: isMatureGeneration && model.is_mature,
        use_nsfw_model: useNsfwModel && model.is_mature,
        use_explicit_nudity: useExplicitNudity && useNsfwModel && model.is_mature
      };
      if (envImage?.id) {
        payload.environment_image_id = envImage.id;
      }
      if (useBodyRef && model.body_references?.length > 0) {
        payload.use_body_reference = true;
        payload.body_ref_strength = bodyRefStrength / 100;
      }
      if (naturalQuality) {
        payload.natural_quality = true;
      }
      if (strictFaceMode) {
        payload.strict_face_mode = true;
      }
      if (hardNudity) {
        payload.hard_nudity = true;
        payload.use_explicit_nudity = true; // hard_nudity implies explicit
      }
      
      // Heavy scenarios → route through async batch (batch_count=1) to avoid CF 100s timeout
      // Triggered when: strict_face_mode, body_reference, or model with reference_image+explicit_nudity
      const isHeavyScenario = strictFaceMode || useBodyRef || hardNudity || (model.reference_image && useExplicitNudity);
      const useAsyncPath = batchMode || isHeavyScenario;
      
      // Batch mode - multiple variations OR single heavy generation (avoid CF timeout)
      if (useAsyncPath) {
        const total = batchMode ? 5 : 1;
        const batchPayload = { ...payload, batch_count: total };
        const batchRes = await axios.post(`${API}/models/${id}/generate-batch`, batchPayload);
        const batchId = batchRes.data.batch_id;
        const realTotal = batchRes.data.total;
        if (batchMode) {
          toast.success(`✨ Generiranje ${realTotal} slika pokrenuto — može potrajati ${realTotal} min`);
        } else {
          toast.success('🔒 Generiranje slike pokrenuto u sigurnom načinu (može trajati do 2 min)...');
        }
        setBatchProgress({ completed: 0, total: realTotal, status: 'processing' });
        
        // Poll batch progress
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API}/batch/${batchId}/status`);
            const s = statusRes.data;
            setBatchProgress({ completed: s.completed, total: s.total, status: s.status });
            
            if (s.completed > 0) fetchModelData(); // Refresh gallery with new images
            
            if (s.status === 'completed' || s.status === 'failed') {
              clearInterval(pollInterval);
              setBatchProgress(null);
              setGenerating(false);
              fetchModelData();
              if (s.completed > 0) {
                toast.success(`${s.completed}/${s.total} ${batchMode ? 'varijacija' : 'slika'} generirano!`);
              } else {
                toast.error("Generiranje neuspješno — pokušajte ponovo");
              }
            }
          } catch {
            clearInterval(pollInterval);
            setBatchProgress(null);
            setGenerating(false);
          }
        }, 3000);
        
        setCustomPrompt('');
        setEnvImage(null);
        return;
      }
      
      // Single image mode
      const response = await axios.post(`${API}/models/${id}/generate`, payload);
      const provider = response.data?.provider;
      const warning = response.data?.warning;
      if (warning) {
        toast.info(warning);
      } else if (provider === 'venice.ai' && (useBodyRef || model.reference_image)) {
        toast.info("Slika generirana putem Venice AI fallback-a — lice možda nije identično referenci.");
      } else {
        toast.success(envImage ? t('envImageGenerated') : useNsfwModel ? t('nsfwImageGenerated') : t('imageGenerated'));
      }
      fetchModelData();
      setCustomPrompt('');
      setEnvImage(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Greška pri generiranju slike");
    } finally {
      setGenerating(false);
    }
  };

  const handleEnvImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingEnv(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-environment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const preview = URL.createObjectURL(file);
      setEnvImage({ id: res.data.id, preview, filename: file.name });
      // Auto-enable NSFW model when env image uploaded (required for NinjaChat img2img)
      if (model?.is_mature) {
        setIsMatureGeneration(true);
        setUseNsfwModel(true);
      }
      toast.success("Slika okoline uploadana!");
    } catch (error) {
      toast.error("Greška pri uploadu slike");
    } finally {
      setUploadingEnv(false);
    }
  };

  const handleBodyRefUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if ((model.body_references?.length || 0) >= 3) {
      toast.error("Maksimalno 3 referentne slike tijela");
      return;
    }
    setUploadingBodyRef(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/models/${id}/body-references`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Referentna slika tijela dodana!");
      fetchModelData(); // Refresh to get updated model with new body_references
    } catch (error) {
      toast.error(error.response?.data?.detail || "Greška pri uploadu referentne slike");
    } finally {
      setUploadingBodyRef(false);
    }
  };

  const handleDeleteBodyRef = async (refId) => {
    try {
      await axios.delete(`${API}/models/${id}/body-references/${refId}`);
      toast.success("Referentna slika tijela obrisana");
      fetchModelData();
    } catch (error) {
      toast.error("Greška pri brisanju");
    }
  };


  const handleGenerateVideo = async () => {
    if (!selectedImageForVideo) {
      toast.error("Odaberite sliku za video");
      return;
    }
    setGeneratingVideo(true);
    setVideoDialogOpen(false);
    try {
      const response = await axios.post(`${API}/models/${id}/generate-video`, {
        image_id: selectedImageForVideo,
        prompt: videoPrompt,
        video_model: videoModel
      });
      toast.success("Video generiranje pokrenuto! Traje ~1 minutu. Provjeravajte galeriju.");
      
      // Start polling for video status
      if (response.data?.id) {
        pollVideoStatus(response.data.id);
      }
      
      fetchModelData();
      setVideoPrompt('gentle natural movement, cinematic lighting');
      setSelectedImageForVideo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Greška pri generiranju videa");
    } finally {
      setGeneratingVideo(false);
    }
  };

  const pollVideoStatus = (videoId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/videos/${videoId}/status`);
        if (res.data.status === 'completed') {
          clearInterval(interval);
          toast.success("Video je spreman!");
          fetchModelData();
        } else if (res.data.status === 'failed') {
          clearInterval(interval);
          toast.error(`Video generiranje neuspješno: ${res.data.error || 'Nepoznata greška'}`);
          fetchModelData();
        } else {
          // Update progress in local state
          setVideos(prev => prev.map(v => 
            v.id === videoId ? {...v, progress: res.data.progress} : v
          ));
        }
      } catch {
        clearInterval(interval);
      }
    }, 10000); // Poll every 10 seconds
  };

  const handleGenerateLipsync = async () => {
    if (!selectedImageForVideo || !audioUrl) {
      toast.error("Odaberite sliku i unesite audio URL");
      return;
    }
    setGeneratingLipsync(true);
    setLipsyncDialogOpen(false);
    try {
      const response = await axios.post(`${API}/models/${id}/lipsync`, {
        image_id: selectedImageForVideo,
        audio_url: audioUrl
      });
      toast.success(t('lipsyncStarted'));
      fetchModelData();
      setAudioUrl('');
      setTtsText('');
      setTtsAudioPreview(null);
      setSelectedImageForVideo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Greška pri generiranju lipsync videa");
    } finally {
      setGeneratingLipsync(false);
    }
  };

  const handleGenerateTts = async () => {
    if (!ttsText.trim()) {
      toast.error("Unesite tekst za govor");
      return;
    }
    setGeneratingTts(true);
    try {
      let response;
      if (ttsProvider === 'elevenlabs') {
        response = await axios.post(`${API}/tts/elevenlabs/generate`, {
          text: ttsText,
          voice_id: elevenVoiceId,
          stability: 0.5,
          similarity_boost: 0.75
        });
      } else {
        response = await axios.post(`${API}/tts/generate`, {
          text: ttsText,
          voice: ttsVoice,
          model: ttsModel
        });
      }
      const { audio_url, audio_base64 } = response.data;
      setAudioUrl(audio_url);
      setTtsAudioPreview(`data:audio/mpeg;base64,${audio_base64}`);
      toast.success(`Govor generiran (${ttsProvider === 'elevenlabs' ? 'ElevenLabs' : 'OpenAI'})!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Greška pri generiranju govora");
    } finally {
      setGeneratingTts(false);
    }
  };

  const handleGenerateTalkingVideo = async () => {
    if (!selectedImageForVideo || !talkingText.trim()) {
      toast.error("Odaberite sliku i unesite tekst");
      return;
    }
    setGeneratingTalking(true);
    setTalkingDialogOpen(false);
    try {
      await axios.post(`${API}/models/${id}/generate-talking-video`, {
        image_id: selectedImageForVideo,
        text: talkingText,
        voice: talkingProvider === 'openai' ? talkingVoice : talkingElevenVoiceId,
        tts_provider: talkingProvider,
        movement_prompt: talkingMovement
      });
      toast.success(t('talkingStarted'));
      fetchModelData();
      setTalkingText('');
      setSelectedImageForVideo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Greška pri generiranju govoreće videa");
    } finally {
      setGeneratingTalking(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await axios.delete(`${API}/images/${imageId}`);
      toast.success("Slika obrisana");
      setImages(images.filter(img => img.id !== imageId));
    } catch (error) {
      toast.error("Greška pri brisanju slike");
    }
  };

  const handleSetReference = async (imageId) => {
    try {
      await axios.post(`${API}/models/${id}/set-reference/${imageId}`);
      toast.success("Referentna slika postavljena! Sve buduće slike će imati isti lik.");
      fetchModelData();
    } catch (error) {
      toast.error("Greška pri postavljanju referentne slike");
    }
  };

  const handleClearReference = async () => {
    try {
      await axios.delete(`${API}/models/${id}/reference`);
      toast.success("Referentna slika uklonjena. Sljedeća generacija će stvoriti novi lik.");
      fetchModelData();
    } catch (error) {
      toast.error("Greška pri uklanjanju referentne slike");
    }
  };

  const handleDeleteVideo = async (videoId) => {
    try {
      await axios.delete(`${API}/videos/${videoId}`);
      toast.success("Video obrisan");
      setVideos(videos.filter(v => v.id !== videoId));
    } catch (error) {
      toast.error("Greška pri brisanju videa");
    }
  };

  const openCarousel = (index) => {
    setCarouselIndex(index);
    setCarouselOpen(true);
  };

  const openVideoDialog = (imageId) => {
    setSelectedImageForVideo(imageId);
    setVideoDialogOpen(true);
  };

  const openLipsyncDialog = (imageId) => {
    setSelectedImageForVideo(imageId);
    setLipsyncDialogOpen(true);
  };

  const openTalkingDialog = (imageId) => {
    setSelectedImageForVideo(imageId);
    setTalkingDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!model) return null;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="main-content">
        {/* Mobile Header */}
        <div className="flex items-center justify-between mb-8 md:hidden">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-text-secondary hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Sparkles className="w-7 h-7 text-primary" />
          <LanguageSwitcher />
        </div>

        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('back')}
        </Button>

        {/* Model Header */}
        <div className="flex flex-col lg:flex-row gap-8 mb-8">
          {/* Model Preview */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="glass rounded-2xl overflow-hidden">
              {model.thumbnail ? (
                <img 
                  src={`data:image/png;base64,${model.thumbnail}`}
                  alt={model.name}
                  className="w-full aspect-[3/4] object-cover"
                />
              ) : (
                <div className="w-full aspect-[3/4] bg-surface-highlight flex items-center justify-center">
                  <Users className="w-20 h-20 text-text-secondary" />
                </div>
              )}
            </div>
          </div>

          {/* Model Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold">{model.name}</h1>
                  {model.is_mature && (
                    <span className="mature-badge">18+</span>
                  )}
                </div>
                <p className="text-text-secondary">{model.description || 'Nema opisa'}</p>
              </div>
              <Button 
                variant="outline"
                onClick={() => navigate(`/models/${id}/edit`)}
                className="border-white/10"
                data-testid="edit-model-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Uredi
              </Button>
            </div>

            {/* Characteristics */}
            <div className="glass rounded-xl p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <span className="text-text-secondary text-sm">Spol</span>
                <p className="font-medium capitalize">{model.gender}</p>
              </div>
              <div>
                <span className="text-text-secondary text-sm">Dob</span>
                <p className="font-medium">{model.age_range}</p>
              </div>
              <div>
                <span className="text-text-secondary text-sm">Etnicitet</span>
                <p className="font-medium capitalize">{model.ethnicity}</p>
              </div>
              <div>
                <span className="text-text-secondary text-sm">Kosa</span>
                <p className="font-medium capitalize">{model.hair_color} {model.hair_style}</p>
              </div>
              <div>
                <span className="text-text-secondary text-sm">Oči</span>
                <p className="font-medium capitalize">{model.eye_color}</p>
              </div>
              <div>
                <span className="text-text-secondary text-sm">Stil</span>
                <p className="font-medium capitalize">{model.style}</p>
              </div>
            </div>

            {/* Character Consistency Info */}
            {model.reference_image && (
              <div className="mt-4 p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-primary/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-primary" />
                    <span className="font-medium">Konzistentnost lika aktivna</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleClearReference}
                    className="border-white/10 text-text-secondary hover:text-white"
                    data-testid="clear-reference-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Novi lik
                  </Button>
                </div>
                <p className="text-text-secondary text-sm mt-1">
                  Sve nove NSFW slike koriste NinjaChat za isti izgled lica. Kliknite "Novi lik" za generiranje potpuno novog lika (Venice AI).
                </p>
              </div>
            )}
            {!model.reference_image && model.is_mature && (
              <div className="mt-4 p-3 bg-surface-highlight rounded-xl border border-border">
                <p className="text-text-secondary text-sm">
                  Prva generirana slika automatski postaje referenca za konzistentnost lika.
                </p>
              </div>
            )}

            {/* LoRA Identity Training — works for both old & new models */}
            <LoraTraining
              modelId={model.id}
              modelName={model.name}
              onStatusChange={() => fetchModelData()}
            />

            {/* Body Reference Images */}
            <div className="mt-4 p-4 bg-surface-highlight rounded-xl border border-border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-secondary" />
                    Referenca tijela ({(model.body_references || []).length}/3)
                  </p>
                  <p className="text-text-secondary text-xs mt-1">
                    Uploadajte 1-3 slike osobe kao bazu za građu/tijelo. Lice će biti promijenjeno.
                  </p>
                </div>
              </div>
              
              {/* Body Reference Grid */}
              <div className="flex gap-2 flex-wrap">
                {bodyRefPreviews.map((ref) => (
                  <div key={ref.id} className="relative group w-20 h-28 rounded-lg overflow-hidden border border-border">
                    {ref.image_base64 ? (
                      <img 
                        src={`data:image/png;base64,${ref.image_base64}`} 
                        alt={ref.filename || 'Body ref'} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-surface flex items-center justify-center">
                        <Users className="w-6 h-6 text-text-secondary" />
                      </div>
                    )}
                    <button
                      onClick={() => handleDeleteBodyRef(ref.id)}
                      className="absolute top-0.5 right-0.5 p-1 bg-black/70 rounded-full text-error opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-body-ref-${ref.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {/* Upload button */}
                {(model.body_references || []).length < 3 && (
                  <label className="w-20 h-28 rounded-lg border-2 border-dashed border-border hover:border-secondary/50 cursor-pointer flex flex-col items-center justify-center transition-colors"
                    data-testid="body-ref-upload"
                  >
                    {uploadingBodyRef ? (
                      <Loader2 className="w-5 h-5 text-secondary animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-5 h-5 text-text-secondary" />
                        <span className="text-[10px] text-text-secondary mt-1">Dodaj</span>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleBodyRefUpload} className="hidden" />
                  </label>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={() => setGenerateDialogOpen(true)}
              disabled={generating}
              className="mt-6 bg-gradient-to-r from-primary to-secondary hover:opacity-90 btn-glow"
              data-testid="generate-image-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {batchProgress 
                    ? (batchProgress.completed === 0
                        ? `Pokrećem generiranje ${batchProgress.total} slika... (do 1 min po slici)`
                        : `Generirano ${batchProgress.completed}/${batchProgress.total} — nastavlja...`)
                    : 'Generiranje... (može potrajati do 1 min)'
                  }
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generiraj novu sliku
                </>
              )}
            </Button>
            
            {/* Batch Progress Bar */}
            {batchProgress && (
              <div className="mt-3 space-y-2">
                <div className="w-full bg-border rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500 rounded-full"
                    style={{ 
                      width: batchProgress.completed === 0 
                        ? '5%' 
                        : `${(batchProgress.completed / batchProgress.total) * 100}%` 
                    }}
                  />
                </div>
                <p className="text-xs text-text-secondary text-center">
                  {batchProgress.completed === 0
                    ? `⏳ Generiram 1. od ${batchProgress.total} slika... (~1 min po slici)`
                    : `✓ ${batchProgress.completed}/${batchProgress.total} slika generirano`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Gallery Section */}
        <div>
          {/* Tabs */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => setActiveTab('images')}
              className={`text-2xl font-bold pb-2 border-b-2 transition-all ${
                activeTab === 'images' ? 'border-primary text-white' : 'border-transparent text-text-secondary hover:text-white'
              }`}
            >
              Galerija ({images.length})
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`text-2xl font-bold pb-2 border-b-2 transition-all ${
                activeTab === 'videos' ? 'border-primary text-white' : 'border-transparent text-text-secondary hover:text-white'
              }`}
            >
              {t('videos')} ({videos.length})
            </button>
          </div>
          
          {activeTab === 'images' ? (
            <>
              {images.length === 0 ? (
                <div className="glass rounded-2xl p-12 text-center" data-testid="empty-gallery">
                  <ImageIcon className="w-16 h-16 mx-auto text-text-secondary mb-4" />
                  <h3 className="text-lg font-bold mb-2">Nema slika</h3>
                  <p className="text-text-secondary mb-6">Generirajte prvu sliku vašeg modela</p>
                  <Button 
                    onClick={() => setGenerateDialogOpen(true)}
                    className="bg-gradient-to-r from-primary to-secondary hover:opacity-90"
                    data-testid="empty-gallery-generate-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('generateImage')}
                  </Button>
                </div>
              ) : (
                <div className="gallery-grid" data-testid="gallery-grid">
                  {images.map((image, index) => (
                    <div key={image.id} className="relative group flex flex-col">
                      <div className="relative">
                        <img 
                          src={`${API}/images/${image.id}/raw`}
                          alt="Generated"
                          className="gallery-image"
                          onClick={() => openCarousel(index)}
                          data-testid={`gallery-image-${image.id}`}
                        />
                        {image.is_mature && (
                          <span className="mature-badge absolute top-2 right-2 text-xs">18+</span>
                        )}
                        {image.provider_used && (
                          <span
                            className="absolute bottom-2 left-2 text-[9px] font-mono px-1.5 py-0.5 rounded bg-black/70 text-white/90"
                            title={`Pipeline: ${image.provider_used}`}
                            data-testid={`provider-badge-${image.id}`}
                          >
                            {image.provider_used.includes('img2img') ? 'P1 img2img' :
                             image.provider_used.includes('direct') ? 'P2 direct' :
                             image.provider_used.includes('face-swap') ? 'P3 hybrid' :
                             image.provider_used.slice(0, 12)}
                          </span>
                        )}
                        <div className="absolute bottom-2 left-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openVideoDialog(image.id);
                            }}
                            className="flex-1 p-2 rounded-lg bg-accent/80 text-white text-xs font-medium hover:bg-accent flex items-center justify-center gap-1"
                            title="Generiraj video iz slike"
                          >
                            <Video className="w-3 h-3" />
                            Video
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openLipsyncDialog(image.id);
                            }}
                            className="flex-1 p-2 rounded-lg bg-secondary/80 text-white text-xs font-medium hover:bg-secondary flex items-center justify-center gap-1"
                            title="Generiraj lipsync video"
                          >
                            <Mic className="w-3 h-3" />
                            Lipsync
                          </button>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openTalkingDialog(image.id);
                            }}
                            className="flex-1 p-2 rounded-lg bg-gradient-to-r from-accent/80 to-secondary/80 text-white text-xs font-medium hover:opacity-90 flex items-center justify-center gap-1"
                            title="Govoreći video (pokret + lipsync)"
                            data-testid={`talking-video-${image.id}`}
                          >
                            <Play className="w-3 h-3" />
                            Govoreći Video
                          </button>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetReference(image.id);
                            }}
                            className="flex-1 p-2 rounded-lg bg-primary/80 text-white text-xs font-medium hover:bg-primary flex items-center justify-center gap-1"
                            title="Postavi kao referentnu sliku"
                            data-testid={`set-reference-${image.id}`}
                          >
                            <Star className="w-3 h-3" />
                            Referenca
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const link = document.createElement('a');
                              link.href = `${API}/images/${image.id}/raw`;
                              link.download = `model-${model?.name || 'image'}-${image.id.slice(0,8)}.png`;
                              link.click();
                              toast.success('Slika preuzeta!');
                            }}
                            className="p-2 rounded-lg bg-black/60 text-green-400 hover:bg-green-500/20"
                            title="Preuzmi sliku"
                            data-testid={`download-image-${image.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(image.id);
                            }}
                            className="p-2 rounded-lg bg-black/60 text-error hover:bg-error/20"
                            data-testid={`delete-image-${image.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        </div>
                      </div>
                      {image.prompt && (
                        <div 
                          className="mt-1 p-2 bg-surface-highlight rounded-b-xl text-xs text-text-secondary cursor-pointer hover:text-white transition-colors"
                          onClick={() => {
                            const ta = document.createElement('textarea');
                            ta.value = image.prompt;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                            toast.success('Prompt kopiran!');
                          }}
                          title="Kliknite za kopiranje"
                          data-testid={`prompt-${image.id}`}
                        >
                          <p className="line-clamp-2">{image.prompt}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {videos.length === 0 ? (
                <div className="glass rounded-2xl p-12 text-center">
                  <Video className="w-16 h-16 mx-auto text-text-secondary mb-4" />
                  <h3 className="text-lg font-bold mb-2">Nema videa</h3>
                  <p className="text-text-secondary mb-6">Odaberite sliku i generirajte video</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video) => (
                    <div key={video.id} className="relative group glass rounded-xl overflow-hidden">
                      {video.status === 'queued' || video.status === 'processing' ? (
                        <div className="w-full aspect-[9/16] flex flex-col items-center justify-center bg-surface-highlight">
                          <Loader2 className="w-12 h-12 text-accent animate-spin mb-4" />
                          <p className="text-white font-medium text-lg">
                            {video.progress ? `${Math.min(video.progress, 99)}%` : 'Pokretanje...'}
                          </p>
                          <p className="text-text-secondary text-sm mt-1">
                            {video.status === 'queued' ? 'U redu čekanja' : 'Generiranje videa'}
                          </p>
                          {video.progress > 0 && (
                            <div className="w-3/4 mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-accent rounded-full transition-all duration-500"
                                style={{width: `${Math.min(video.progress, 99)}%`}}
                              />
                            </div>
                          )}
                        </div>
                      ) : video.status === 'failed' ? (
                        <div className="w-full aspect-[9/16] flex flex-col items-center justify-center bg-surface-highlight">
                          <X className="w-12 h-12 text-error mb-4" />
                          <p className="text-error font-medium">Generiranje neuspješno</p>
                          <p className="text-text-secondary text-xs mt-1 px-4 text-center">{video.error || ''}</p>
                          <button
                            onClick={() => handleDeleteVideo(video.id)}
                            className="mt-4 px-4 py-2 rounded-lg bg-error/20 text-error text-sm hover:bg-error/30 transition-colors flex items-center gap-2"
                            data-testid={`delete-failed-video-${video.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            Obriši
                          </button>
                        </div>
                      ) : (
                        <video 
                          src={video.video_url}
                          className="w-full aspect-[9/16] object-cover"
                          controls
                          data-testid={`video-${video.id}`}
                        />
                      )}
                      <div className="absolute top-2 left-2 flex gap-2">
                        {video.video_type === 'lipsync' && (
                          <span className="px-2 py-1 bg-secondary/80 rounded text-xs font-medium">Lipsync</span>
                        )}
                        {video.video_type === 'talking' && (
                          <span className="px-2 py-1 bg-gradient-to-r from-accent/80 to-secondary/80 rounded text-xs font-medium">Govoreći</span>
                        )}
                        {video.video_type === 'image_to_video' && (
                          <span className="px-2 py-1 bg-accent/80 rounded text-xs font-medium">Video</span>
                        )}
                        {video.is_mature && (
                          <span className="mature-badge text-xs">18+</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteVideo(video.id)}
                        className="absolute top-2 right-2 p-2 rounded-lg bg-black/60 text-error opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-video-${video.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {video.video_url && (
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/videos/${video.id}/download`;
                            link.download = `video-${video.id.slice(0,8)}.mp4`;
                            link.click();
                            toast.success('Video preuzimanje pokrenuto!');
                          }}
                          className="absolute top-2 right-12 p-2 rounded-lg bg-black/60 text-green-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Preuzmi video"
                          data-testid={`download-video-${video.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                      {video.prompt && (
                        <div 
                          className="absolute bottom-0 left-0 right-0 p-2 bg-black/80 text-xs text-text-secondary cursor-pointer hover:text-white transition-colors"
                          onClick={() => {
                            const ta = document.createElement('textarea');
                            ta.value = video.prompt;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                            toast.success('Prompt kopiran!');
                          }}
                          title="Kliknite za kopiranje"
                          data-testid={`video-prompt-${video.id}`}
                        >
                          <p className="line-clamp-2">{video.prompt}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Carousel */}
      {carouselOpen && (
        <ImageCarousel 
          images={images} 
          initialIndex={carouselIndex} 
          onClose={() => setCarouselOpen(false)} 
        />
      )}

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="bg-surface border-border max-h-[90dvh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
            <DialogTitle>Generiraj novu sliku</DialogTitle>
            <DialogDescription>
              Dodajte opcionalni prompt za prilagodbu slike
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
            <div>
              <Label>{t('additionalPrompt')}</Label>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="prompt-presets">
                {[
                  { labelKey: "presetGlamour", promptKey: "presetGlamourPrompt" },
                  { labelKey: "presetBeach", promptKey: "presetBeachPrompt" },
                  { labelKey: "presetEvening", promptKey: "presetEveningPrompt" },
                  { labelKey: "presetSport", promptKey: "presetSportPrompt" },
                  { labelKey: "presetOffice", promptKey: "presetOfficePrompt" },
                  { labelKey: "presetCasual", promptKey: "presetCasualPrompt" },
                  { labelKey: "presetSelfie", promptKey: "presetSelfiePrompt" },
                  { labelKey: "presetFitness", promptKey: "presetFitnessPrompt" },
                  { labelKey: "presetBed", promptKey: "presetBedPrompt" },
                  { labelKey: "presetSummer", promptKey: "presetSummerPrompt" },
                  { labelKey: "presetPool", promptKey: "presetPoolPrompt" },
                  { labelKey: "presetNight", promptKey: "presetNightPrompt" },
                ].map((preset) => (
                  <button
                    key={preset.labelKey}
                    type="button"
                    onClick={() => {
                      if (preset.labelKey === 'presetSelfie') {
                        setSelfiePickerOpen(true);
                      } else {
                        setCustomPrompt(prev => prev ? `${prev}, ${t(preset.promptKey)}` : t(preset.promptKey));
                      }
                    }}
                    className="px-2.5 py-1 text-xs rounded-full border border-border bg-surface-highlight hover:bg-primary/20 hover:border-primary/40 text-text-secondary hover:text-white transition-all duration-200 whitespace-nowrap"
                    data-testid={`preset-${preset.labelKey}`}
                  >
                    {preset.labelKey === 'presetSelfie' ? '📱 ' : ''}{t(preset.labelKey)}
                  </button>
                ))}
              </div>
              {selfiePickerOpen && (
                <div className="mt-3 p-3 rounded-lg border border-primary/30 bg-primary/5" data-testid="selfie-location-picker">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{t('selfiePickerTitle')}</span>
                    <button
                      type="button"
                      onClick={() => setSelfiePickerOpen(false)}
                      className="text-xs text-text-secondary hover:text-white"
                      data-testid="selfie-picker-close"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    {[
                      { key: 'office', emoji: '💼' },
                      { key: 'cafe', emoji: '☕' },
                      { key: 'car', emoji: '🚗' },
                      { key: 'beach', emoji: '🏖️' },
                      { key: 'bedroom', emoji: '🛏️' },
                      { key: 'bathroom', emoji: '🪞' },
                      { key: 'park', emoji: '🌳' },
                      { key: 'restaurant', emoji: '🍽️' },
                      { key: 'gym', emoji: '🏋️' },
                      { key: 'pool', emoji: '🏊' },
                      { key: 'street', emoji: '🌆' },
                      { key: 'home', emoji: '🏠' },
                    ].map(loc => (
                      <button
                        key={loc.key}
                        type="button"
                        onClick={() => {
                          const scene = t(`selfieLoc_${loc.key}`);
                          const fullPrompt = `selfie, ${scene}`;
                          setCustomPrompt(fullPrompt);
                          setSelfiePickerOpen(false);
                        }}
                        className="px-2 py-1.5 text-xs rounded-md border border-border bg-surface-highlight hover:bg-primary/30 hover:border-primary text-text-secondary hover:text-white transition-all"
                        data-testid={`selfie-loc-${loc.key}`}
                      >
                        {loc.emoji} {t(`selfieLoc_${loc.key}`)}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-text-secondary mt-2">{t('selfiePickerHint')}</p>
                </div>
              )}
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="npr. u večernjoj haljini, na plaži, profesionalni portret..."
                className="form-input mt-2 min-h-[80px] resize-none"
                data-testid="custom-prompt-input"
              />
            </div>
            
            {model.is_mature && (
              <>
                <div className="flex items-center justify-between p-4 bg-surface-highlight rounded-xl">
                  <div>
                    <p className="font-medium">{t('matureToggle')}</p>
                    <p className="text-text-secondary text-sm">{t('matureToggleDesc')}</p>
                  </div>
                  <Switch 
                    checked={isMatureGeneration}
                    onCheckedChange={(v) => {
                      setIsMatureGeneration(v);
                      if (!v) { setUseNsfwModel(false); setUseExplicitNudity(false); }
                    }}
                    data-testid="mature-generation-switch"
                  />
                </div>
                
                {isMatureGeneration && (
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-secondary/20 to-primary/20 rounded-xl border border-secondary/30">
                    <div>
                      <p className="font-medium text-secondary">NSFW Model</p>
                      <p className="text-text-secondary text-sm">
                        {model.reference_image 
                          ? "NinjaChat (konzistentnost lika aktivna)" 
                          : "Venice AI (bez cenzure modela)"}
                      </p>
                    </div>
                    <Switch 
                      checked={useNsfwModel}
                      onCheckedChange={(v) => {
                        setUseNsfwModel(v);
                        if (!v) setUseExplicitNudity(false);
                      }}
                      data-testid="nsfw-model-switch"
                    />
                  </div>
                )}
                
                {isMatureGeneration && useNsfwModel && (
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-xl border border-red-500/30">
                    <div>
                      <p className="font-medium text-red-400">{t('nudityToggle')}</p>
                      <p className="text-text-secondary text-sm">
                        {t('nudityDesc')}
                      </p>
                    </div>
                    <Switch 
                      checked={useExplicitNudity}
                      onCheckedChange={setUseExplicitNudity}
                      data-testid="explicit-nudity-switch"
                    />
                  </div>
                )}

                {isMatureGeneration && useNsfwModel && !useExplicitNudity && (
                  <div className="p-3 bg-secondary/10 rounded-lg text-sm text-text-secondary border border-secondary/20">
                    <span className="text-secondary font-medium">{t('info')}:</span> {t('nsfwNoNudityInfo')}
                  </div>
                )}
                
                {isMatureGeneration && useNsfwModel && model.reference_image && (
                  <div className="p-3 bg-primary/10 rounded-lg text-sm text-text-secondary border border-primary/20">
                    <span className="text-primary font-medium">Info:</span> Referentna slika je aktivna - slika će imati isti lik. Za potpuno novi lik, uklonite referentnu sliku.
                  </div>
                )}
              </>
            )}
            
            {/* Natural Quality Toggle */}
            <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-400">Prirodna kvaliteta</p>
                  <p className="text-text-secondary text-xs mt-0.5">
                    Fotorealistična slika — izgleda kao prava osoba, ne AI
                  </p>
                </div>
                <Switch 
                  checked={naturalQuality}
                  onCheckedChange={setNaturalQuality}
                  data-testid="natural-quality-switch"
                />
              </div>
              {naturalQuality && (
                <div className="mt-2 p-2 bg-green-500/5 rounded-lg text-xs text-text-secondary">
                  Dodaje: prirodna tekstura kože, pore, nesavršenosti, iPhone foto stil, bez airbrushing-a
                </div>
              )}
            </div>
            
            {/* Strict Face Mode Toggle - only visible when model has reference_image */}
            {model.reference_image && (
              <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="font-medium text-amber-400">🔒 {t('strictFaceMode')}</p>
                    <p className="text-text-secondary text-xs mt-0.5">
                      {t('strictFaceModeDesc')}
                    </p>
                  </div>
                  <Switch 
                    checked={strictFaceMode}
                    onCheckedChange={setStrictFaceMode}
                    data-testid="strict-face-mode-switch"
                  />
                </div>
                {strictFaceMode && (
                  <div className="mt-2 p-2 bg-amber-500/5 rounded-lg text-xs text-text-secondary">
                    {t('strictFaceModeHint')}
                  </div>
                )}
              </div>
            )}
            
            {/* Hard Nudity Mode Toggle - 2-stage explicit + face-swap */}
            {model.reference_image && model.is_mature && (
              <div className="p-4 bg-gradient-to-r from-red-500/10 to-pink-500/10 rounded-xl border border-red-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="font-medium text-red-400">🔥 {t('hardNudity')}</p>
                    <p className="text-text-secondary text-xs mt-0.5">
                      {t('hardNudityDesc')}
                    </p>
                  </div>
                  <Switch
                    checked={hardNudity}
                    onCheckedChange={setHardNudity}
                    data-testid="hard-nudity-switch"
                  />
                </div>
                {hardNudity && (
                  <div className="mt-2 p-2 bg-red-500/5 rounded-lg text-xs text-text-secondary">
                    {t('hardNudityHint')}
                  </div>
                )}
              </div>
            )}
            
            {/* Environment Image Upload */}
            <div className="p-4 bg-surface-highlight rounded-xl border border-border">
              <Label className="block mb-2">Slika okoline (opcionalno)</Label>
              <p className="text-text-secondary text-xs mb-3">Uploadajte sliku pozadine/okoline u kojoj želite generirati model</p>
              
              {envImage ? (
                <div className="relative">
                  <img src={envImage.preview} alt="Okolina" className="w-full h-32 object-cover rounded-lg" />
                  <button
                    onClick={() => setEnvImage(null)}
                    className="absolute top-1 right-1 p-1 bg-black/70 rounded-full text-white hover:bg-error transition-colors"
                    data-testid="remove-env-image"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-text-secondary mt-1">{envImage.filename}</p>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                  data-testid="env-image-upload"
                >
                  {uploadingEnv ? (
                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                  ) : (
                    <>
                      <ImageIcon className="w-6 h-6 text-text-secondary mb-1" />
                      <span className="text-xs text-text-secondary">Kliknite za upload slike</span>
                    </>
                  )}
                  <input type="file" accept="image/*" onChange={handleEnvImageUpload} className="hidden" />
                </label>
              )}
            </div>
            
            {/* Body Reference Toggle — only show when model is in CREATION mode (no reference yet) */}
            {/* For already-created characters (with reference_image), this option is hidden because it
                generates a NEW face on the body — which would ruin the established character */}
            {model.body_references && model.body_references.length > 0 && !model.reference_image && (
              <div className="p-4 bg-gradient-to-r from-secondary/10 to-accent/10 rounded-xl border border-secondary/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-secondary">Referenca tijela</p>
                    <p className="text-text-secondary text-xs mt-0.5">
                      Čuva tijelo/građu iz slike, generira novo lice
                    </p>
                  </div>
                  <Switch 
                    checked={useBodyRef}
                    onCheckedChange={setUseBodyRef}
                    data-testid="body-ref-switch"
                  />
                </div>
                {useBodyRef && (
                  <div className="mt-3 space-y-3">
                    <div className="flex gap-2">
                      {filteredBodyRefs.map(ref => (
                        <img 
                          key={ref.id}
                          src={`data:image/png;base64,${ref.image_base64}`}
                          alt="Body ref"
                          className="w-12 h-16 object-cover rounded border border-secondary/40"
                        />
                      ))}
                      <p className="text-xs text-text-secondary self-center ml-1">
                        {model.body_references.length} referenc{model.body_references.length === 1 ? 'a' : 'e'}
                      </p>
                    </div>
                    
                    {/* Strength Slider */}
                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-text-secondary">Promjena lica</span>
                        <span className="text-xs font-mono text-secondary">{bodyRefStrength}%</span>
                      </div>
                      <input 
                        type="range" 
                        min="30" max="90" 
                        value={bodyRefStrength}
                        onChange={(e) => setBodyRefStrength(Number(e.target.value))}
                        className="w-full h-1.5 bg-border rounded-lg appearance-none cursor-pointer accent-secondary"
                        data-testid="body-ref-strength-slider"
                      />
                      <div className="flex justify-between text-[10px] text-text-secondary">
                        <span>Manja promjena</span>
                        <span>Veća promjena</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Batch Mode - 5 Variations */}
            <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-amber-400">5 varijacija</p>
                  <p className="text-text-secondary text-xs mt-0.5">
                    Isti prompt, ista prostorija — 5 različitih kutova i poza
                  </p>
                </div>
                <Switch 
                  checked={batchMode}
                  onCheckedChange={setBatchMode}
                  data-testid="batch-mode-switch"
                />
              </div>
              {batchMode && (
                <div className="mt-2 p-2 bg-amber-500/5 rounded-lg text-xs text-text-secondary">
                  Generirat će se 5 slika: prednji, lijevi, desni kut, pogled odozdo, pogled preko ramena — sve u istoj sceni
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t border-border">
            <Button 
              variant="outline" 
              onClick={() => setGenerateDialogOpen(false)}
              className="border-white/10"
              data-testid="cancel-generate-btn"
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleGenerate}
              className="bg-gradient-to-r from-primary to-secondary hover:opacity-90"
              data-testid="confirm-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {batchMode ? 'Generiraj 5 slika' : 'Generiraj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="image-modal"
          onClick={() => setSelectedImage(null)}
          data-testid="image-modal"
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <button
            className="absolute top-6 right-20 text-white/60 hover:text-green-400 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              const link = document.createElement('a');
              link.href = `${API}/images/${selectedImage.id}/raw`;
              link.download = `model-image-${selectedImage.id?.slice(0,8) || 'full'}.png`;
              link.click();
              toast.success('Slika preuzeta!');
            }}
            data-testid="modal-download-image"
            title="Preuzmi sliku"
          >
            <Download className="w-8 h-8" />
          </button>
          <img 
            src={`${API}/images/${selectedImage.id}/raw`}
            alt="Full size"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Video Generation Dialog */}
      <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
        <DialogContent className="bg-surface border-border max-h-[90dvh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-accent" />
              Generiraj Video
            </DialogTitle>
            <DialogDescription>
              Pretvorite sliku u video s pokretom
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
            <div>
              <Label>Prompt za pokret</Label>
              <textarea
                value={videoPrompt}
                onChange={(e) => setVideoPrompt(e.target.value)}
                placeholder="npr. gentle natural movement, hair flowing in wind, subtle smile..."
                className="form-input mt-2 min-h-[80px] resize-none"
                data-testid="video-prompt-input"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Video model</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setVideoModel('kling')}
                  className={`p-3 rounded-xl border text-left transition-all ${videoModel === 'kling' ? 'border-accent bg-accent/10 text-white' : 'border-border bg-surface-highlight text-text-secondary hover:border-white/30'}`}
                  data-testid="video-model-kling"
                >
                  <p className="font-medium text-sm">Seedance 1.5 Pro</p>
                  <p className="text-xs mt-1 opacity-70">Najbolja kvaliteta, ~3-5 min</p>
                </button>
                <button
                  onClick={() => setVideoModel('ovi')}
                  className={`p-3 rounded-xl border text-left transition-all ${videoModel === 'ovi' ? 'border-accent bg-accent/10 text-white' : 'border-border bg-surface-highlight text-text-secondary hover:border-white/30'}`}
                  data-testid="video-model-ovi"
                >
                  <p className="font-medium text-sm">Seedance 1.5 Pro (brzi)</p>
                  <p className="text-xs mt-1 opacity-70">Kraći timeout, ~1-3 min</p>
                </button>
              </div>
            </div>
            
            <div className="p-3 bg-surface-highlight rounded-xl">
              <p className="text-sm text-text-secondary">
                <span className="text-accent font-medium">Info:</span> Seedance 1.5 Pro daje kvalitetne video rezultate. Pišite promptove samo za pokret (npr. "slowly turns head and smiles"), ne opisujte izgled osobe — lik se automatski čuva iz slike.
              </p>
            </div>
          </div>
          
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t border-border">
            <Button 
              variant="outline" 
              onClick={() => setVideoDialogOpen(false)}
              className="border-white/10"
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleGenerateVideo}
              disabled={generatingVideo}
              className="bg-accent hover:bg-accent/90"
              data-testid="confirm-video-btn"
            >
              {generatingVideo ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generiranje...
                </>
              ) : (
                <>
                  <Video className="w-4 h-4 mr-2" />
                  Generiraj Video
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lipsync Dialog */}
      <Dialog open={lipsyncDialogOpen} onOpenChange={setLipsyncDialogOpen}>
        <DialogContent className="bg-surface border-border max-h-[90dvh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-secondary" />
              {t('lipsyncTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('lipsyncDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
            {/* TTS Section */}
            <div className="p-4 bg-gradient-to-r from-secondary/10 to-primary/10 rounded-xl border border-secondary/30">
              <p className="font-medium text-secondary mb-3">{t('generateSpeech')}</p>
              <textarea
                value={ttsText}
                onChange={(e) => setTtsText(e.target.value)}
                placeholder="Unesite tekst koji želite pretvoriti u govor..."
                className="form-input min-h-[80px] resize-none"
                maxLength={5000}
                data-testid="tts-text-input"
              />
              <p className="text-xs text-text-secondary mt-1">{ttsText.length}/5000 znakova</p>
              
              {/* Provider selector */}
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setTtsProvider('openai')}
                  className={`flex-1 p-2 rounded-lg text-xs font-medium transition-all ${ttsProvider === 'openai' ? 'bg-primary/20 border border-primary/40 text-white' : 'bg-surface-highlight border border-border text-text-secondary'}`}
                  data-testid="tts-provider-openai"
                >
                  OpenAI TTS
                </button>
                <button
                  type="button"
                  onClick={() => setTtsProvider('elevenlabs')}
                  className={`flex-1 p-2 rounded-lg text-xs font-medium transition-all ${ttsProvider === 'elevenlabs' ? 'bg-purple-500/20 border border-purple-500/40 text-white' : 'bg-surface-highlight border border-border text-text-secondary'}`}
                  data-testid="tts-provider-elevenlabs"
                >
                  ElevenLabs
                </button>
              </div>

              {ttsProvider === 'openai' && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Glas</Label>
                    <select value={ttsVoice} onChange={(e) => setTtsVoice(e.target.value)} className="form-input mt-1 text-sm" data-testid="tts-voice-select">
                      <option value="nova">Nova (energična)</option>
                      <option value="shimmer">Shimmer (vedra)</option>
                      <option value="alloy">Alloy (neutralna)</option>
                      <option value="coral">Coral (topla)</option>
                      <option value="echo">Echo (mirna)</option>
                      <option value="fable">Fable (ekspresivna)</option>
                      <option value="onyx">Onyx (duboka)</option>
                      <option value="sage">Sage (mudra)</option>
                      <option value="ash">Ash (jasna)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Kvaliteta</Label>
                    <select value={ttsModel} onChange={(e) => setTtsModel(e.target.value)} className="form-input mt-1 text-sm" data-testid="tts-model-select">
                      <option value="tts-1-hd">HD (bolja kvaliteta)</option>
                      <option value="tts-1">Standard (brže)</option>
                    </select>
                  </div>
                </div>
              )}

              {ttsProvider === 'elevenlabs' && (
                <div className="mt-3">
                  <Label className="text-xs">ElevenLabs Glas</Label>
                  <select value={elevenVoiceId} onChange={(e) => setElevenVoiceId(e.target.value)} className="form-input mt-1 text-sm" data-testid="eleven-voice-select">
                    <option value="0jvpZ98RZwx5FBOSZAc3">Moj glas (custom)</option>
                    <option value="21m00Tcm4TlvDq8ikWAM">Rachel (ženski, američki)</option>
                    <option value="EXAVITQu4vr4xnSDxMaL">Bella (ženski, američki)</option>
                    <option value="MF3mGyEYCl7XYWbV9V6O">Elli (ženski, američki)</option>
                    <option value="TxGEqnHWrfWFTfGW9XjX">Josh (muški, američki)</option>
                    <option value="VR6AewLTigWG4xSOukaG">Arnold (muški, američki)</option>
                    <option value="pNInz6obpgDQGcFmaJgB">Adam (muški, američki)</option>
                    <option value="ErXwobaYiN019PkySvjV">Antoni (muški, američki)</option>
                    <option value="jBpfuIE2acCO8z3wKNLl">Gigi (ženski, američki)</option>
                    <option value="onwK4e9ZLuTAKqWW03F9">Daniel (muški, britanski)</option>
                  </select>
                  <p className="text-xs text-text-secondary mt-1">Višejezični model — podržava hrvatski</p>
                </div>
              )}
              
              <Button
                onClick={handleGenerateTts}
                disabled={generatingTts || !ttsText.trim()}
                className="w-full mt-3 bg-secondary hover:bg-secondary/90"
                data-testid="generate-tts-btn"
              >
                {generatingTts ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generiranje govora...
                  </>
                ) : (
                  <>
                    <Volume2 className="w-4 h-4 mr-2" />
                    Generiraj govor
                  </>
                )}
              </Button>
              
              {ttsAudioPreview && (
                <div className="mt-3 p-3 bg-surface rounded-lg">
                  <p className="text-xs text-text-secondary mb-2">Preslušajte generirani govor:</p>
                  <audio controls src={ttsAudioPreview} className="w-full h-10" data-testid="tts-audio-preview" />
                </div>
              )}
            </div>
            
            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-text-secondary">ILI</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            
            {/* Manual Audio URL */}
            <div>
              <Label>Audio URL (ručni unos)</Label>
              <Input
                value={audioUrl}
                onChange={(e) => { setAudioUrl(e.target.value); setTtsAudioPreview(null); }}
                placeholder="https://example.com/audio.mp3"
                className="mt-2 bg-surface-highlight border-border"
                data-testid="audio-url-input"
              />
              <p className="text-xs text-text-secondary mt-1">
                Direktan link na MP3 ili WAV audio datoteku
              </p>
            </div>
            
            {audioUrl && (
              <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                <p className="text-sm text-green-400 font-medium">Audio spreman za lipsync!</p>
                <p className="text-xs text-text-secondary mt-1 truncate">{audioUrl}</p>
              </div>
            )}

            <div className="p-3 bg-surface-highlight rounded-xl">
              <p className="text-sm text-text-secondary">
                <span className="text-secondary font-medium">Savjet:</span> Za najbolje rezultate koristite čist govor bez pozadinske glazbe. Audio do 60 sekundi.
              </p>
            </div>
          </div>
          
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t border-border">
            <Button 
              variant="outline" 
              onClick={() => setLipsyncDialogOpen(false)}
              className="border-white/10"
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleGenerateLipsync}
              disabled={generatingLipsync || !audioUrl}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="confirm-lipsync-btn"
            >
              {generatingLipsync ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generiranje...
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4 mr-2" />
                  {t('generateLipsync')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Talking Video Dialog */}
      <Dialog open={talkingDialogOpen} onOpenChange={setTalkingDialogOpen}>
        <DialogContent className="bg-surface border-border max-h-[90dvh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-accent" />
              {t('talkingTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('talkingDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
            <div>
              <Label>Tekst za govor *</Label>
              <textarea
                value={talkingText}
                onChange={(e) => setTalkingText(e.target.value)}
                placeholder="Unesite što želite da lik kaže..."
                className="form-input mt-2 min-h-[100px] resize-none"
                maxLength={4096}
                data-testid="talking-text-input"
              />
              <p className="text-xs text-text-secondary mt-1">{talkingText.length}/5000 znakova</p>
            </div>

            {/* Provider selector */}
            <div className="flex gap-2">
              <button type="button" onClick={() => setTalkingProvider('openai')}
                className={`flex-1 p-2 rounded-lg text-xs font-medium transition-all ${talkingProvider === 'openai' ? 'bg-primary/20 border border-primary/40 text-white' : 'bg-surface-highlight border border-border text-text-secondary'}`}>
                OpenAI TTS
              </button>
              <button type="button" onClick={() => setTalkingProvider('elevenlabs')}
                className={`flex-1 p-2 rounded-lg text-xs font-medium transition-all ${talkingProvider === 'elevenlabs' ? 'bg-purple-500/20 border border-purple-500/40 text-white' : 'bg-surface-highlight border border-border text-text-secondary'}`}>
                ElevenLabs
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Glas</Label>
                {talkingProvider === 'openai' ? (
                  <select value={talkingVoice} onChange={(e) => setTalkingVoice(e.target.value)} className="form-input mt-1 text-sm" data-testid="talking-voice-select">
                    <option value="nova">Nova (energična)</option>
                    <option value="shimmer">Shimmer (vedra)</option>
                    <option value="alloy">Alloy (neutralna)</option>
                    <option value="coral">Coral (topla)</option>
                    <option value="echo">Echo (mirna)</option>
                    <option value="fable">Fable (ekspresivna)</option>
                    <option value="onyx">Onyx (duboka)</option>
                    <option value="sage">Sage (mudra)</option>
                    <option value="ash">Ash (jasna)</option>
                  </select>
                ) : (
                  <select value={talkingElevenVoiceId} onChange={(e) => setTalkingElevenVoiceId(e.target.value)} className="form-input mt-1 text-sm">
                    <option value="0jvpZ98RZwx5FBOSZAc3">Moj glas (custom)</option>
                    <option value="21m00Tcm4TlvDq8ikWAM">Rachel (ženski)</option>
                    <option value="EXAVITQu4vr4xnSDxMaL">Bella (ženski)</option>
                    <option value="MF3mGyEYCl7XYWbV9V6O">Elli (ženski)</option>
                    <option value="TxGEqnHWrfWFTfGW9XjX">Josh (muški)</option>
                    <option value="VR6AewLTigWG4xSOukaG">Arnold (muški)</option>
                    <option value="pNInz6obpgDQGcFmaJgB">Adam (muški)</option>
                    <option value="onwK4e9ZLuTAKqWW03F9">Daniel (muški, britanski)</option>
                  </select>
                )}
              </div>
              <div>
                <Label className="text-xs">Pokret</Label>
                <select
                  value={talkingMovement}
                  onChange={(e) => setTalkingMovement(e.target.value)}
                  className="form-input mt-1 text-sm"
                  data-testid="talking-movement-select"
                >
                  <option value="gentle natural head movement, subtle body sway, cinematic">{t('movementNatural')}</option>
                  <option value="energetic head movements, expressive gestures, lively body language">{t('movementEnergetic')}</option>
                  <option value="minimal movement, calm breathing, professional interview style">{t('movementMinimal')}</option>
                  <option value="playful movements, smiling, flirtatious body language">{t('movementPlayful')}</option>
                </select>
              </div>
            </div>

            <div className="p-4 bg-surface-highlight rounded-xl space-y-2">
              <p className="text-sm font-medium text-accent">{t('howItWorks')}</p>
              <div className="space-y-1 text-xs text-text-secondary">
                <p>{t('step1')}</p>
                <p>{t('step2')}</p>
                <p>{t('step3')}</p>
              </div>
              <p className="text-xs text-text-secondary mt-2">{t('duration')}</p>
            </div>
          </div>

          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setTalkingDialogOpen(false)}
              className="border-white/10"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleGenerateTalkingVideo}
              disabled={generatingTalking || !talkingText.trim()}
              className="bg-gradient-to-r from-accent to-secondary hover:opacity-90"
              data-testid="confirm-talking-video-btn"
            >
              {generatingTalking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Pokretanje...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {t('generateTalkingBtn')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default ModelDetail;
